import { Button } from '@/components/ui/button';
import Icon from '@/icons/icon';

interface Props {
    adults: number;
    setAdults: (value: number) => void;
    childrenCount: number;
    setChildrenCount: (value: number) => void;
    rooms: number;
    setRooms: (value: number) => void;
    guestsOpen: boolean;
    setGuestsOpen: (open: boolean) => void;
    guestsRef: React.RefObject<HTMLDivElement | null>;
}

export function SearchGuestsPicker({
    adults,
    setAdults,
    childrenCount: children,
    setChildrenCount: setChildren,
    rooms,
    setRooms,
    guestsOpen,
    setGuestsOpen,
    guestsRef,
}: Props) {
    const guestLabel = `${adults} Adult${adults > 1 ? 's' : ''} • ${children} Child${children !== 1 ? 'ren' : ''} • ${rooms} Room${rooms > 1 ? 's' : ''}`;

    return (
        <div className="relative flex w-full shrink-0 items-center gap-2 border-t px-4 py-4 font-semibold text-black select-none sm:w-auto sm:border-t-0 sm:border-l dark:text-foreground">
            <Icon iconName="user" className="h-5 w-5 stroke-gray-500" />

            <div
                onClick={() => setGuestsOpen(!guestsOpen)}
                className="cursor-pointer text-gray-900"
            >
                {guestLabel}
            </div>

            {guestsOpen && (
                <div
                    ref={guestsRef}
                    className="absolute top-full right-0 z-50 mt-1 w-full rounded-lg bg-card p-4 shadow-2xl sm:w-80"
                >
                    {/* ADULTS */}
                    <div className="flex items-center justify-between py-1">
                        <span className="font-medium">Adults</span>
                        <div className="flex items-center gap-4">
                            <Button
                                variant="bare"
                                onClick={() =>
                                    setAdults(Math.max(1, adults - 1))
                                }
                                disabled={adults <= 1}
                                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded border border-gray-300 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                -
                            </Button>
                            <span className="w-4 text-center font-medium tabular-nums">
                                {adults}
                            </span>

                            <Button
                                variant="bare"
                                onClick={() =>
                                    setAdults(Math.min(rooms * 2, adults + 1))
                                }
                                disabled={adults >= rooms * 2}
                                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded border border-gray-300 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                +
                            </Button>
                        </div>
                    </div>

                    {/* CHILDREN */}
                    <div className="flex items-center justify-between py-1">
                        <span className="font-medium">Children</span>
                        <div className="flex items-center gap-4">
                            <Button
                                variant="bare"
                                onClick={() =>
                                    setChildren(Math.max(0, children - 1))
                                }
                                disabled={children <= 0}
                                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded border border-gray-300 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                -
                            </Button>
                            <span className="w-4 text-center font-medium tabular-nums">
                                {children}
                            </span>
                            <Button
                                variant="bare"
                                onClick={() =>
                                    setChildren(
                                        Math.min(rooms * 2, children + 1),
                                    )
                                }
                                disabled={children >= rooms * 2}
                                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded border border-gray-300 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                +
                            </Button>
                        </div>
                    </div>

                    {/* ROOMS */}
                    <div className="flex items-center justify-between py-1">
                        <span className="font-medium">Rooms</span>
                        <div className="flex items-center gap-4">
                            <Button
                                variant="bare"
                                onClick={() => {
                                    const newRooms = Math.max(1, rooms - 1);
                                    // Scale guests proportionally
                                    const adultRatio = adults / rooms;
                                    const childRatio = children / rooms;
                                    setAdults(
                                        Math.max(
                                            1,
                                            Math.round(newRooms * adultRatio),
                                        ),
                                    );
                                    setChildren(
                                        Math.round(newRooms * childRatio),
                                    );
                                    setRooms(newRooms);
                                }}
                                disabled={rooms <= 1}
                                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded border border-gray-300 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                -
                            </Button>
                            <span className="w-4 text-center font-medium tabular-nums">
                                {rooms}
                            </span>

                            <Button
                                variant="bare"
                                onClick={() => {
                                    const newRooms = rooms + 1;
                                    // Scale guests proportionally
                                    const adultRatio = adults / rooms;
                                    const childRatio = children / rooms;
                                    setAdults(
                                        Math.max(
                                            1,
                                            Math.round(newRooms * adultRatio),
                                        ),
                                    );
                                    setChildren(
                                        Math.round(newRooms * childRatio),
                                    );
                                    setRooms(newRooms);
                                }}
                                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded border border-gray-300 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                +
                            </Button>
                        </div>
                    </div>

                    <Button
                        variant="bare"
                        className="mt-4 w-full cursor-pointer rounded-lg bg-blue-600 py-2 font-semibold text-white transition hover:bg-blue-700"
                        onClick={() => setGuestsOpen(false)}
                    >
                        Done
                    </Button>
                </div>
            )}
        </div>
    );
}
